import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { Login } from "./pages/Login";
import { TrainerPay } from "./pages/TrainerPay";
import { Catalog } from "./pages/Catalog";
import { Home } from "./pages/Home";
import { Cli } from "./pages/Cli";
import { Mcp } from "./pages/Mcp";
import { WhatsappSignup } from "./pages/WhatsappSignup";
import { Calendar } from "./pages/Calendar";
import { Data, Members } from "./pages/Data";
import { ConfigRedirect } from "./pages/ConfigRedirect";
import { StudioConfig } from "./pages/StudioConfig";
import { Flows } from "./pages/Flows";
import { Automations } from "./pages/Automations";
import { Agents } from "./pages/Agents";
import { FlowEdit } from "./pages/FlowEdit";
import { EmailTemplates } from "./pages/EmailTemplates";
import { Newsletter } from "./pages/Newsletter";
import { Campaigns } from "./pages/Campaigns";
import { EmailTemplateEdit } from "./pages/EmailTemplateEdit";
import { MemberDetail } from "./pages/MemberDetail";
import { TrainerDetail } from "./pages/TrainerDetail";
import { Tasks } from "./pages/Tasks";
import { TaskDetail } from "./pages/TaskDetail";
import { Pr } from "./pages/Pr";
import { Inbox } from "./pages/Inbox";
import { InboxThreadDetail } from "./pages/InboxThreadDetail";
import { Decisions } from "./pages/Decisions";
import { Errors, ErrorIncidentDetail } from "./pages/Errors";
import { Assets } from "./pages/Assets";
import { InstagramFunnel } from "./pages/InstagramFunnel";
import { Rota } from "./pages/Rota";
import { Payroll } from "./pages/Payroll";
import { TrainerPerformance } from "./pages/TrainerPerformance";
import { PrPersonDetail } from "./pages/PrPersonDetail";
import { Preload } from "./pages/Preload";
import { TrainerToday } from "./pages/TrainerToday";
import { Efficiency } from "./pages/Efficiency";
import { Analytics } from "./pages/Analytics";
import { ConfigSuggestions } from "./pages/ConfigSuggestions";
import { KnowledgeBaseSuggestions } from "./pages/KnowledgeBaseSuggestions";
import { Status } from "./pages/Status";
import { Reports } from "./pages/Reports";
import { RequireAuth } from "./components/RequireAuth";
import { DashboardLayout } from "./components/DashboardLayout";

/**
 * `/outreach/:id` → `/pr/people/:id`, preserving the contact id. Written as a
 * component rather than a `<Navigate to>` because the target depends on the
 * route param, which only a hook can read.
 */
function LegacyOutreachRedirect(): JSX.Element {
  const { id = "" } = useParams();
  return (
    <Navigate to={`/pr/people/${encodeURIComponent(id)}`} replace />
  );
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* A trainer's own provisional pay, opened from a signed link. OUTSIDE
          <RequireAuth> deliberately: its viewer is a trainer, who has no
          dashboard login at all — the token in the path is the capability.
          Registered in page-registry.ts's UNGATED_ROUTES with its reasoning,
          so it is a written exemption rather than an omission. */}
      <Route path="/trainer-pay/:token" element={<TrainerPay />} />
      <Route element={<RequireAuth />}>
        <Route element={<DashboardLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/cli" element={<Cli />} />
          <Route path="/mcp" element={<Mcp />} />
          <Route path="/whatsapp-signup" element={<WhatsappSignup />} />
          <Route path="/flows" element={<Flows />} />
          <Route path="/automations" element={<Automations />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/flows/:flow_name/edit" element={<FlowEdit />} />
          <Route path="/email-templates" element={<EmailTemplates />} />
          <Route path="/email-templates/:kind/edit" element={<EmailTemplateEdit />} />
          <Route path="/newsletter" element={<Newsletter />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/data" element={<Navigate to="/data/members" replace />} />
          <Route path="/data/:entity" element={<Data />} />
          <Route path="/members" element={<Members />} />
          <Route path="/members/:id" element={<MemberDetail />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/tasks/:id" element={<TaskDetail />} />
          <Route path="/pr" element={<Pr />} />
          <Route path="/pr/people/:id" element={<PrPersonDetail />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/inbox/threads/:id" element={<InboxThreadDetail />} />
          <Route path="/decisions" element={<Decisions />} />
          <Route path="/errors" element={<Errors />} />
          <Route path="/errors/:id" element={<ErrorIncidentDetail />} />
          <Route path="/assets" element={<Assets />} />
          <Route path="/instagram-funnel" element={<InstagramFunnel />} />
          <Route path="/rota" element={<Rota />} />
          <Route path="/payroll" element={<Payroll />} />
          <Route path="/trainer-performance" element={<TrainerPerformance />} />
          {/* `/outreach` was the pre-Peter PR board. The links live in old
              Telegram digests and approval messages, so redirect rather than
              404 — the destination is the same data, reorganised. */}
          <Route path="/outreach" element={<Navigate to="/pr" replace />} />
          <Route
            path="/outreach/:id"
            element={<LegacyOutreachRedirect />}
          />
          <Route path="/preload" element={<Preload />} />
          <Route path="/status" element={<Status />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/efficiency" element={<Efficiency />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route
            path="/efficiency/suggestions"
            element={
              <div>
                <h1 className="page-title">Config suggestions</h1>
                <ConfigSuggestions />
              </div>
            }
          />
          <Route
            path="/efficiency/knowledge"
            element={
              <div>
                <h1 className="page-title">Knowledge base suggestions</h1>
                <KnowledgeBaseSuggestions />
              </div>
            }
          />
          <Route path="/trainers/:id/today" element={<TrainerToday />} />
          <Route path="/trainers/:id" element={<TrainerDetail />} />
          <Route path="/config" element={<ConfigRedirect />} />
          <Route path="/config/:slug" element={<StudioConfig />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
